import { Injectable, InjectionToken, Injector } from '@angular/core';
import {
    AnnualReportNavigationProvider,
    AnnualReportProvider,
    AnnualReportStepsHelper
} from '@modeso/aumico-lib-annualreport-fe';
import { SettingsProvider } from '@modeso/aumico-lib-settings-fe';
import { OrganizationProvider } from '@modeso/aumico-lib-users-fe';
import {
    FinancialStatementTypes,
    IAnnualReport,
    IFinStatements,
    IFinancialStatementSource,
    ISetupData
} from '@modeso/types__aumico-lib-annualreport-be';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import Debug from 'debug';
import { filter, mergeMap, withLatestFrom } from 'rxjs/operators';

const debug = Debug('aumico:saas:AnnualReportOrchestrator');

export const ANNUALREPORT_ORCHESTRATOR_TOKEN = new InjectionToken<string[]>('ANNUALREPORT_ORCHESTRATOR_TOKEN');
@UntilDestroy()
@Injectable({
    providedIn: 'root',
})
export class AnnualReportOrchestrator {
    private settingsProvider: SettingsProvider;
    private organizationProvider: OrganizationProvider;
    private annualReportProvider: AnnualReportProvider;
    private annualReportNavigationProvider: AnnualReportNavigationProvider;

    constructor(injector: Injector) {
        this.settingsProvider = injector.get(SettingsProvider);
        this.organizationProvider = injector.get(OrganizationProvider);
        this.annualReportProvider = injector.get(AnnualReportProvider);
        this.annualReportNavigationProvider = injector.get(AnnualReportNavigationProvider);

        // AnnualReportOrchestrator it's a singleton, so it can subscribe forever

        this.organizationProvider
            .getCurrentOrganization$()
            .pipe(
                filter((organization) => organization != null),
                mergeMap(() => this.annualReportProvider.getActiveAnnualReport$()),
                filter((activeAnnual: IAnnualReport) => activeAnnual != null),
                withLatestFrom(this.annualReportProvider.getAnnualReportIsSealed$()),
                untilDestroyed(this),
            )
            .subscribe(([annualReport, isReadonly]) => {
                if (!isReadonly) {
                    this.handleActiveAnnualReportChange(annualReport);
                }
            });
        debug('AnnualReportOrchestrator initilized');
    }

    requestCurrencyFromBackend() {
        this.settingsProvider.dispatchGetAllCurrency();
        debug('requestSettingsFromBackend');
    }

    load(annualReportId: string) {
        this.annualReportProvider.dispatchActiveAnnualReport(annualReportId);
    }

    new(setupData: ISetupData) {
        const balanceFinancialStatementSource: IFinancialStatementSource = {} as IFinancialStatementSource;
        const incomeFinancialStatementSource: IFinancialStatementSource = {} as IFinancialStatementSource;
        balanceFinancialStatementSource.type = FinancialStatementTypes.BALANCE;
        this.annualReportProvider.setBalanceFinancialStatementSource(balanceFinancialStatementSource);
        incomeFinancialStatementSource.type = FinancialStatementTypes.INCOME;
        this.annualReportProvider.setIncomeFinancialStatementSource(incomeFinancialStatementSource);
        this.annualReportProvider.createAnnualReport(setupData);
    }

    // Internal an probably be moved to an effect in annual report library!
    handleActiveAnnualReportChange(annualReport: IAnnualReport) {
        this.annualReportNavigationProvider.setActiveArNavigation(
            [...AnnualReportStepsHelper.getTemplateStepsOrDefault(annualReport)],
            annualReport.isSealed
        );
        // It's fine to access directly the .finStatements of annual report
        if (annualReport.finStatements.length === 2) {
            annualReport.finStatements.forEach((finStatement: IFinStatements) => {
                if (finStatement.type === 'balance') {
                    debug('load balance finstatement import data');
                    this.annualReportProvider.setBalanceFinancialStatementSource(finStatement.import);
                } else if (finStatement.type === 'income') {
                    debug('load income finstatement import data');
                    this.annualReportProvider.setIncomeFinancialStatementSource(finStatement.import);
                }
            });
        }
    }
}
